import { createContext, useContext, useState, useEffect, ReactNode } from 'react';

interface User {
    id: string;
    email: string;
    fullName: string;
    role: 'ADMIN' | 'MANAGER' | 'USER';
}

interface AuthContextType {
    user: User | null;
    loading: boolean;
    login: (email: string, password: string) => Promise<void>;
    register: (email: string, password: string, fullName: string, role?: string) => Promise<void>;
    logout: () => void;
    accessToken: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
import { API_BASE_URL } from '../config/api';

const API_URL = API_BASE_URL;

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const storedToken = localStorage.getItem('accessToken');

        if (storedToken) {
            setAccessToken(storedToken);
            fetchCurrentUser(storedToken);
        } else {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    async function fetchCurrentUser(token: string) {
        try {
            const response = await fetch(API_URL + '/api/auth/me', {
                headers: {
                    Authorization: 'Bearer ' + token,
                },
            });

            if (response.ok) {
                const data: any = await response.json();
                setUser(data.user);
            } else {
                await refreshAccessToken();
            }
        } catch (error) {
            console.error('Failed to fetch current user:', error);
        } finally {
            setLoading(false);
        }
    }

    async function refreshAccessToken() {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(API_URL + '/api/auth/refresh', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ refreshToken }),
            });

            if (response.ok) {
                const data: any = await response.json();
                localStorage.setItem('accessToken', data.accessToken);
                setAccessToken(data.accessToken);
                await fetchCurrentUser(data.accessToken);
            } else {
                logout();
            }
        } catch (error) {
            console.error('Failed to refresh token:', error);
            logout();
        }
    }

    async function login(email: string, password: string) {
        const response = await fetch(API_URL + '/api/auth/login', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password }),
        });

        if (!response.ok) {
            const error: any = await response.json();
            throw new Error(error.error || 'Login failed');
        }

        const data: any = await response.json();

        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        setAccessToken(data.accessToken);
        setUser(data.user);
    }

    async function register(
        email: string,
        password: string,
        fullName: string,
        role: string = 'USER'
    ) {
        const response = await fetch(API_URL + '/api/auth/register', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ email, password, fullName, role }),
        });

        if (!response.ok) {
            const error: any = await response.json();
            throw new Error(error.error || 'Registration failed');
        }

        const data: any = await response.json();

        localStorage.setItem('accessToken', data.accessToken);
        localStorage.setItem('refreshToken', data.refreshToken);
        setAccessToken(data.accessToken);
        setUser(data.user);
    }

    function logout() {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        setAccessToken(null);
        setUser(null);
    }

    const value = { user, loading, login, register, logout, accessToken };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
